import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, computed, inject, signal, viewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { KENDO_PROGRESSBAR, ProgressBarAnimation } from '@progress/kendo-angular-progressbar';
import { ScrollViewModule, ScrollViewComponent } from '@progress/kendo-angular-scrollview';
import { StandaloneDefaultImports } from 'src/app/shared/ui/standalone-default-imports';

export interface NewFeaturesDialogData {
    language: string;
    version: string;
    features: any[];
    modalTitle: any;
    executeAction?: (actionName: string) => void;
}

@Component({
    selector: 'new-features',
    imports: [...StandaloneDefaultImports, MatDialogModule, ScrollViewModule, KENDO_PROGRESSBAR],
    templateUrl: './new-features.component.html',
    styleUrls: ['./new-features.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewFeaturesComponent {
    // Section: SERVICES / DEPENDENCIES
    //----------------------------------------------------------------------
    private readonly dialogRef = inject(MatDialogRef<NewFeaturesComponent>);
    private readonly translate = inject(TranslateService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);
    public readonly data: NewFeaturesDialogData = inject(MAT_DIALOG_DATA);

    // Section: SIGNALS, OBSERVABLES AND STATE REACTIVE
    //----------------------------------------------------------------------
    public readonly progressValue = signal<number>(0);
    public readonly progressAnimation = signal<ProgressBarAnimation | boolean>({
        duration: 8800
    });

    public readonly features = computed<any[]>(() => {
        return this.data.features.map((feature) => ({
            ...feature,
            featureTitle: feature.title[this.data.language],
            tag: feature.tag ? feature.tag[this.data.language] : '',
            featureDescription: feature.description[this.data.language],
            featureTextButton: feature.ctaText[this.data.language],
            ctaClasses: feature.ctaClasses || '',
            gradientBackground: feature.background
                ? feature.background
                : `linear-gradient(133.11deg, ${feature.color_primary} -1.51%, ${feature.color_secondary} 100%)`,
            backgroundImageUrl: `url(${feature.image})`,
            hasSecondaryCta: !!(feature.secondaryCta && feature.secondaryCta.action),
            secondaryCtaText: feature.secondaryCta && feature.secondaryCta.ctaText ? feature.secondaryCta.ctaText[this.data.language] : '',
            secondaryCtaAction: feature.secondaryCta ? feature.secondaryCta.action : '',
            secondaryCtaClasses: feature.secondaryCta ? feature.secondaryCta.classes : '',
            secondaryCtaIcon: feature.secondaryCta ? feature.secondaryCta.iconClass : ''
        }));
    });

    public readonly hasMultipleFeatures = computed<boolean>(() => this.features().length > 1);

    public readonly currentIndex = signal<number>(0);

    public readonly mainTitle = computed<string>(() => {
        return this.data.modalTitle ? this.data.modalTitle[this.data.language] : this.translate.instant('global.whatsNew');
    });

    public readonly isLastSlide = computed<boolean>(() => {
        const feats = this.features();
        return feats.length < 2 || this.currentIndex() === feats.length - 1;
    });

    public readonly actionBtnText = computed<string>(() => {
        return this.isLastSlide() ? this.translate.instant('global.form.upToDate') : this.translate.instant('global.form.next');
    });

    // Section: PRIVATE PROPERTIES
    //----------------------------------------------------------------------
    private readonly whatsNewView = viewChild<ScrollViewComponent>('whatsNew');
    private intervalId?: ReturnType<typeof setInterval>;
    private timeoutId?: ReturnType<typeof setTimeout>;

    constructor() {
        this.timeoutId = setTimeout(() => {
            this.progressValue.set(100);
        }, 1200);

        this.intervalId = this.setSliderInterval();

        this.destroyRef.onDestroy(() => {
            clearInterval(this.intervalId);
            clearTimeout(this.timeoutId);
        });
    }

    // Section: PUBLIC METHODS
    //----------------------------------------------------------------------
    onItemChanged(event: any): void {
        this.currentIndex.set(event.index);

        clearInterval(this.intervalId);
        this.intervalId = this.setSliderInterval();

        this.resetProgress();

        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
            this.progressAnimation.set({ duration: 9600 });
            this.progressValue.set(100);
        }, 100);
    }

    nextSlide(slider: ScrollViewComponent): void {
        slider.next();
    }

    onCancel(): void {
        this.dialogRef.close({ closed: true });
    }

    onSecondaryCtaClick(actionName: string): void {
        if (this.data.executeAction) {
            this.data.executeAction(actionName);
        }
    }

    // Section: PRIVATE METHODS
    //----------------------------------------------------------------------
    private resetProgress(): void {
        this.progressAnimation.set(false);
        this.progressValue.set(0);
    }

    private setSliderInterval(): ReturnType<typeof setInterval> {
        return setInterval(() => {
            this.whatsNewView()?.next();
            this.cdr.markForCheck();
        }, 10000);
    }
}
